import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Send, AtSign } from 'lucide-react';

/**
 * Epic 18.1: Comment input with @mention support.
 * Typing @ triggers a dropdown of active system users.
 * Selecting a user inserts their handle (e.g., @Sarah).
 */
const CommentInput = ({ onSave, teamMembers = [], placeholder = 'Add internal note... Type @ to mention' }) => {
    const [value, setValue] = useState('');
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionStart, setMentionStart] = useState(-1);
    const [mentionFilter, setMentionFilter] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const textareaRef = useRef(null);
    const dropdownRef = useRef(null);

    const filteredUsers = teamMembers.filter((u) => {
        const name = (u.email || '').split('@')[0] || '';
        const email = (u.email || '').toLowerCase();
        const q = mentionFilter.toLowerCase();
        return name.toLowerCase().includes(q) || email.includes(q);
    });

    const handleChange = (e) => {
        const v = e.target.value;
        setValue(v);
        const cursorPos = e.target.selectionStart;
        const beforeCursor = v.slice(0, cursorPos);
        const lastAt = beforeCursor.lastIndexOf('@');
        if (lastAt >= 0) {
            const afterAt = beforeCursor.slice(lastAt + 1);
            if (!/\s/.test(afterAt)) {
                setShowMentionDropdown(true);
                setMentionStart(lastAt);
                setMentionFilter(afterAt);
                setSelectedIndex(0);
                return;
            }
        }
        setShowMentionDropdown(false);
    };

    const insertMention = useCallback((user) => {
        const before = value.slice(0, mentionStart);
        const after = value.slice(textareaRef.current?.selectionStart ?? value.length);
        const handle = user.email?.split('@')[0] || user.email || 'User';
        const newValue = `${before}@${handle} ${after}`;
        setValue(newValue);
        setShowMentionDropdown(false);
        textareaRef.current?.focus();
        setTimeout(() => {
            const pos = mentionStart + handle.length + 2;
            textareaRef.current?.setSelectionRange(pos, pos);
        }, 0);
    }, [value, mentionStart]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!textareaRef.current || !textareaRef.current.contains(e.target)) return;
            if (!showMentionDropdown || filteredUsers.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && filteredUsers.length > 0) {
                e.preventDefault();
                insertMention(filteredUsers[selectedIndex]);
            } else if (e.key === 'Escape') {
                setShowMentionDropdown(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showMentionDropdown, filteredUsers, selectedIndex, insertMention, textareaRef]);

    const extractMentionedUserIds = () => {
        const ids = [];
        // Match @handle (letters, digits, dots, hyphens, plus) - e.g. @john, @john.doe, @subham+1
        const re = /@([\w.+-]+)/g;
        let m;
        while ((m = re.exec(value)) !== null) {
            const handle = m[1].toLowerCase().trim();
            if (!handle) continue;
            // Prefer exact match (e.g. @subham+1 must match subham+1, not subham)
            const exactMatch = teamMembers.find((u) => {
                const email = (u.email || '').toLowerCase();
                const namePart = email.split('@')[0] || '';
                return namePart === handle || email === handle;
            });
            const user = exactMatch || teamMembers.find((u) => {
                const namePart = (u.email || '').split('@')[0]?.toLowerCase() || '';
                return namePart.startsWith(handle);
            });
            if (user?.id && !ids.includes(user.id)) ids.push(user.id);
        }
        return ids;
    };

    const handleSubmit = () => {
        const trimmed = value.trim();
        if (!trimmed) return;
        const mentionedUserIds = extractMentionedUserIds();
        onSave({ content: trimmed, mentionedUserIds });
        setValue('');
    };

    return (
        <div className="relative">
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onBlur={() => setTimeout(() => setShowMentionDropdown(false), 200)}
                placeholder={placeholder}
                className="min-h-[80px] resize-none rounded-lg border-amber-200 bg-amber-50/50 focus:border-amber-400 focus:ring-amber-200"
                rows={3}
            />
            {showMentionDropdown && filteredUsers.length > 0 && (
                <div
                    ref={dropdownRef}
                    className="absolute z-50 mt-1 max-h-40 w-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg"
                >
                    {filteredUsers.map((user, i) => (
                        <button
                            key={user.id}
                            type="button"
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 ${i === selectedIndex ? 'bg-amber-50' : ''
                                }`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                insertMention(user);
                            }}
                        >
                            <span className="font-medium">@{user.email?.split('@')[0] || user.email}</span>
                            <span className="ml-2 text-slate-500 text-xs">{user.email}</span>
                        </button>
                    ))}
                </div>
            )}
            <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-slate-500 flex items-center gap-1">
                    <AtSign className="h-3 w-3" /> Type @ to mention a teammate
                </span>
                <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!value.trim()}
                    className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white"
                >
                    <Send className="h-3 w-3 mr-1" />
                    Save
                </Button>
            </div>
        </div>
    );
};

export default CommentInput;
